import React, { useEffect, useState } from "react";
import { Box, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ReferralSchema from "../../../../Schema/Referral.Schema";
import { CallApiWithToken } from "../../../../api/ApiRequest";
import { api_submitReferral } from "../../../../api/APIEndpoint";
import { getReferralCode } from "../../../../utils/Utils";
import TextBox from "../../../../components/TextField";
import Button from "../../../../components/Button";
import classNames from "classnames";
import Alert from "../../../../components/Alert";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function ReferralScreen({ className, handleReferralForm }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    FullName: "",
    Email: "",
    PhoneNumber: "",
    Street: "",
    City: "",
    State: "",
    PostalCode: "",
    Message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  useEffect(() => {
    setFormData({
      FullName: "",
      Email: "",
      PhoneNumber: "",
      Street: "",
      City: "",
      State: "",
      PostalCode: "",
      Message: "",
    });
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser || null);
    });
    return () => unsubscribe();
  }, []);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const isValidPhoneNumber = (phone) => {
    const sanitizedPhone = phone.replace(/[^0-9]/g, "");
    return sanitizedPhone.length === 10 && !/^(\d)\1{10}$/.test(sanitizedPhone);
  };

  const submitHandler = async () => {
    const { error } = ReferralSchema.validate(formData);
    if (error) {
      error.details.forEach((err) => {
        setAlert({
          open: true,
          message: error.details[0].message,
          severity: "warning",
        });
      });
      return;
    }

    if (!isValidPhoneNumber(formData.PhoneNumber)) {
      setAlert({
        open: true,
        message: "Please enter a valid phone number.",
        severity: "warning",
      });
      return;
    }

    const addressFields = [
      formData.Street,
      formData.City,
      formData.State,
      formData.PostalCode,
    ];
    const filledFields = addressFields.filter((field) => field !== "");

    if (filledFields.length > 0 && filledFields.length < 4) {
      setAlert({
        open: true,
        message:
          "If you fill one address field (Street, City, State, PostalCode), you must fill all of them.",
        severity: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      const res = await CallApiWithToken("POST", `${api_submitReferral}`, {
        FullName: formData.FullName,
        Email: formData.Email,
        PhoneNumber: formData.PhoneNumber,
        Message: formData.Message,
        LeadAddress: {
          Street: formData.Street || "1320 Motor Pkwy",
          City: formData.City || "Islandia",
          State: formData.State || "NY",
          PostalCode: formData.PostalCode || "11749",
        },
        ReferralCode: getReferralCode(user?.displayName), // Replace with actual user data
      });

      if (res) {
        setAlert({
          open: true,
          message: "Referral successfully submitted!",
          severity: "success",
        });
        handleReferralForm();
      }
    } catch (err) {
      setAlert({
        open: true,
        message: `Error: ${err.message || "Something went wrong"}`,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={className}>
        <div className="main-container">
          <Box className="container">
            <div className="text">Referral</div>

            <Box>
              <TextBox
                placeholder="Full Name"
                value={formData.FullName}
                onChange={handleChange("FullName")}
              />

              <TextBox
                placeholder="Email"
                value={formData.Email}
                onChange={handleChange("Email")}
              />

              <TextBox
                placeholder="Phone Number"
                value={formData.PhoneNumber}
                onChange={handleChange("PhoneNumber")}
              />

              <TextBox
                placeholder="Message"
                value={formData.Message}
                onChange={handleChange("Message")}
              />

              <Typography variant="subtitle1" className="address">
                Address (Optional)
              </Typography>

              <TextBox
                placeholder="Street"
                value={formData.Street}
                onChange={handleChange("Street")}
              />

              <TextBox
                placeholder="City"
                value={formData.City}
                onChange={handleChange("City")}
              />

              <TextBox
                placeholder="State"
                value={formData.State}
                onChange={handleChange("State")}
              />

              <TextBox
                placeholder="Postal Code"
                type="number"
                value={formData.PostalCode}
                onChange={handleChange("PostalCode")}
              />

              <Button
                label="Submit your referral"
                onClick={submitHandler}
                loading={isLoading}
                disabled={isLoading}
              />
            </Box>
          </Box>
        </div>
        <Alert
          open={alert.open}
          message={alert.message}
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
        />
      </div>
    </>
  );
}

export default styled(ReferralScreen)(() => ({
  ".main-container": {
    display: "flex",
    justifyContent: "center",
  },
  ".container": {
    textAlign: "center",
    padding: "20px",
    maxWidth: "400px",
  },
  ".text": {
    fontWeight: 700,
    fontSize: 60,
    letterSpacing: 0,
    color: "#234000",
  },
  ".input": {
    marginBottom: "15px",
  },
  ".button": {
    marginTop: "20px",
  },
  ".address": {
    textAlign: "left",
    marginTop: 20,
  },
}));
